import { request } from "../request";

const base = `${process.env.REACT_APP_API_SERVER}/esr/organizations`;

export type OrganizationStatus = "PENDING" | "APPROVED";

export interface OrganizationInfo {
	name: string;
	address: string;
	phone: string;
	url: string;
}

export interface Organization extends OrganizationInfo {
	id: number;
	status: OrganizationStatus;
	requested_by: string;
	admins: string[];
	members: string[];
	/** Per-member info; invited = no TREC account with that email yet */
	member_details: Record<
		string,
		{ phone: string; title: string; invited: boolean }
	>;
	/** Whether the current user (site admin or organization admin) can edit it */
	can_manage: boolean;
	created: string;
	updated: string;
}

/** Public summary returned to the (unauthenticated) signup page. */
export type OrganizationSummary = OrganizationInfo & { id: number };

/** Field-level validation errors returned by the backend, e.g. { name: ["...already exists."] } */
export type OrganizationErrors = Partial<
	Record<keyof OrganizationInfo, string[]>
>;

export const emptyOrganizationInfo: OrganizationInfo = {
	name: "",
	address: "",
	phone: "",
	url: "",
};

export async function listOrganizations(params: {
	search?: string;
	status?: OrganizationStatus[];
}) {
	const { data } = await request.get<{
		metadata: { total_count: number; site_admin: boolean };
		data: Organization[];
	}>(`${base}/`, {
		params: {
			search: params.search || undefined,
			status: params.status?.length ? params.status.join(",") : undefined,
		},
	});
	return data;
}

export async function listApprovedOrganizations() {
	const { data } = await request.get<OrganizationSummary[]>(
		`${base}/approved/`,
	);
	return data;
}

/** Unauthenticated: resolve an invitation link token for the signup page. */
export async function getInvitation(token: string) {
	const { data } = await request.get<{
		email: string;
		organization: OrganizationSummary;
	}>(`${base}/invitation/`, { params: { token } });
	return data;
}

/** 400 validation errors are returned as `errors` (not thrown) so forms can show them inline. */
const returnValidationErrors = {
	validateStatus: (s: number) => s < 500 && s !== 401 && s !== 403,
};

type SaveResult = { errors?: OrganizationErrors; organization?: Organization };

function toSaveResult({
	status,
	data,
}: {
	status: number;
	data: unknown;
}): SaveResult {
	if (status === 400) {
		return { errors: data as OrganizationErrors };
	}
	return { organization: data as Organization };
}

export async function createOrganization(info: OrganizationInfo) {
	return toSaveResult(
		await request.post(`${base}/create/`, info, returnValidationErrors),
	);
}

/** Update details (name, address, phone, website). Site admins or organization admins only. */
export async function updateOrganization(id: number, info: OrganizationInfo) {
	return toSaveResult(
		await request.post(`${base}/${id}/update/`, info, returnValidationErrors),
	);
}

/** Enabled (approved) organizations are offered on the signup page; disabled ones are pending. */
export async function setOrganizationEnabled(id: number, enabled: boolean) {
	const { data } = await request.post<Organization>(
		`${base}/${id}/${enabled ? "enable" : "disable"}/`,
	);
	return data;
}

export async function deleteOrganization(id: number) {
	await request.post(`${base}/${id}/delete/`);
}

/** Organizations the current user belongs to. */
export async function getMyOrganizations() {
	const { data } = await request.get<Organization[]>(`${base}/mine/`);
	return data;
}

export async function addOrganizationMember(
	id: number,
	email: string,
	admin = false,
) {
	// invitation_sent: an invitation email went to an address with no TREC account yet
	const { data } = await request.post<
		Organization & { invitation_sent?: boolean }
	>(`${base}/${id}/add_member/`, {
		email,
		admin,
	});
	return data;
}

/** With adminOnly, revokes admin rights but keeps the user as a member. */
export async function removeOrganizationMember(
	id: number,
	email: string,
	adminOnly = false,
) {
	const { data } = await request.post<Organization>(
		`${base}/${id}/remove_member/`,
		{
			email,
			admin_only: adminOnly,
		},
	);
	return data;
}

export interface MemberCandidate {
	email: string;
	name: string;
}

/** Enabled TREC users (same set as User Management) that can be added as members. */
export async function listMemberCandidates() {
	const { data } = await request.get<MemberCandidate[]>(
		`${base}/member_candidates/`,
	);
	return data;
}
